import { apiClient } from './client';
import type { ApiResponse } from './types';

export type ModelProviderType =
  | 'openai'
  | 'azure-openai'
  | 'anthropic'
  | 'deepseek'
  | 'google'
  | 'xai'
  | 'qwen'
  | 'kimi'
  | 'doubao';

export const REASONING_EFFORTS = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh'] as const;
export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'low';

export interface ModelProvider {
  id: number;
  type: ModelProviderType;
  api_key_preview: string;
  base_url: string | null;
  model: string;
  timeout_ms: number | null;
  reasoning_effort: ReasoningEffort;
  created_at: string;
  updated_at: string;
}

export interface GroupMember {
  providerId: number;
  model: string;
  baseUrl: string | null;
  timeoutMs: number | null;
  priority: number;
  isDefault: boolean;
}

export interface ModelProviderGroup {
  id: number;
  name: string;
  target_agents: string[];
  target_capabilities: string[];
  timeout_ms: number | null;
  is_global_default: boolean;
  members: GroupMember[];
  created_at: string;
  updated_at: string;
}

export interface CreateProviderBody {
  type: ModelProviderType;
  apiKey: string;
  baseUrl: string | null;
  model: string;
  timeoutMs: number | null;
  reasoningEffort: ReasoningEffort;
}

export interface PatchProviderBody {
  type?: ModelProviderType;
  apiKey?: string;
  baseUrl?: string | null;
  model?: string;
  timeoutMs?: number | null;
  reasoningEffort?: ReasoningEffort;
}

export interface CreateGroupBody {
  name: string;
  targetAgents: string[];
  timeoutMs: number | null;
  members: { providerId: number; priority: number; isDefault: boolean }[];
}

export type PatchGroupBody = Partial<CreateGroupBody>;

export async function listProviders(): Promise<ModelProvider[]> {
  const r = await apiClient.get<ApiResponse<{ rows: ModelProvider[] }>>('/model-providers');
  return r.data.data!.rows;
}

export async function createProvider(body: CreateProviderBody): Promise<number> {
  const r = await apiClient.post<ApiResponse<{ id: number }>>('/model-providers', body);
  return r.data.data!.id;
}

export async function updateProvider(id: number, body: PatchProviderBody): Promise<void> {
  await apiClient.patch(`/model-providers/${id}`, body);
}

export async function deleteProvider(id: number): Promise<void> {
  await apiClient.delete(`/model-providers/${id}`);
}

export async function testProvider(id: number): Promise<string> {
  const r = await apiClient.post<ApiResponse<{ replyPreview: string }>>(
    `/model-providers/${id}/test`,
    undefined,
    { timeout: 0 },
  );
  return r.data.data!.replyPreview;
}

export async function listGroups(): Promise<ModelProviderGroup[]> {
  const r = await apiClient.get<ApiResponse<{ rows: ModelProviderGroup[] }>>('/model-provider-groups');
  return r.data.data!.rows;
}

export async function createGroup(body: CreateGroupBody): Promise<number> {
  const r = await apiClient.post<ApiResponse<{ id: number }>>('/model-provider-groups', body);
  return r.data.data!.id;
}

export async function updateGroup(id: number, body: PatchGroupBody): Promise<void> {
  await apiClient.patch(`/model-provider-groups/${id}`, body);
}

export async function deleteGroup(id: number): Promise<void> {
  await apiClient.delete(`/model-provider-groups/${id}`);
}

export async function setGroupDefault(id: number): Promise<void> {
  await apiClient.put(`/model-provider-groups/${id}/default`);
}
